/*
 * FOR SALE — avis déposés sur le site après un achat (décision 014). Chargé par le snippet fs-review-form, en fin
 * de page (layout for-sale-demo), APRÈS fs-account.js : session, connexion Discord et appels au service passent par
 * window.FSAccount. Rien n'est affiché ni demandé au service tant que GET /me n'annonce pas `features.reviews`
 * (REVIEWS=1 et champs d'avis présents) ; sans FSAccount, le module ne fait rien.
 *
 * Acheteur :
 *  - lien de l'invitation postée dans le ticket : /?avis=<dossier>. Sans session : seulement « se connecter pour
 *    retrouver votre achat » (texte neutre : on ne sait pas encore si les avis sont ouverts), avec l'intention
 *    { kind: 'account', ticket } gardée par fs-account.js ; au retour, formulaire si /me l'autorise ;
 *  - menu du compte : « Laisser un avis » quand un achat attend son avis (GET /tickets/mine, 60 s de cache d'onglet lié
 *    à la session) ;
 *  - formulaire : note de 1 à 5 (boutons radio, groupe requis), texte de 3 à 1 000 caractères (points de code, après le
 *    même nettoyage NFKC que le service), notice (publication dans #reviews sans pseudo, « Achat vérifié », jeu et mois
 *    d'achat ; tous les avis publiés ; aucune contrepartie). POST /reviews ; tout refus est expliqué.
 * Avis général (« membre », tout membre connecté, sans dossier, sans « Achat vérifié ») : boutons déclaratifs
 *  `<button data-fs-review-open="member" hidden>` rendus par le thème (page /pages/avis, carrousel), démasqués quand
 *  GET /features (public) annonce les avis membres, ouverts au clic par délégation ; ou window.FSReviews.open({ kind:
 *  'member', from }) — le focus revient sur `from` à la fermeture. Sans session : connexion Discord d'abord, intention
 *  gardée 10 min dans l'onglet (« fs-review-member-intent »), formulaire au retour. Si un achat attend son avis, le choix est proposé :
 *  « Avis sur un achat » (vérifié) ou « Avis général ». Délai entre deux avis lu sur /me (features.memberReview).
 *  window.FSReviews.open({ ticket }) ouvre l'avis d'un achat, comme le lien de l'invitation.
 * La remise du compte est « Conclure la vente » (actions de ticket, décision 013) : rien côté équipe ici. Tout texte
 * venu du service passe par textContent ; toute adresse est vérifiée. Les fonctions pures sont exportées pour les
 * tests Node.
 */
import { dialog as uiDialog, dialogHost } from './fs-ui-dialog.js';
import { el as h } from './fs-ui.js';
import { date, number, t } from './fs-i18n.js';

export const TICKET = /^ticket-[0-9a-f]{16}$/;
/** Identifiant d'un avis du site : s… (achat vérifié) ou m… (avis général d'un membre). */
export const REVIEW_ID = /^[sm][0-9a-f]{8}$/;
export const MEMBER_INTENT_KEY = 'fs-review-member-intent';
export const MEMBER_INTENT_MAX_MS = 10 * 60 * 1000;
export const TEXT_MIN = 3;
export const TEXT_MAX = 1000;
export const LINES_MAX = 15;
export const ROWS_KEY = 'fs-review-rows';
export const ROWS_TTL_MS = 60_000;
export const GAME_NAMES = Object.freeze({ waruniverse: 'WarUniverse', redgalaxy: 'Red Galaxy', darkorbit: 'DarkOrbit', novahorizon: 'Nova Horizon' });
export const REVIEW_STATES = Object.freeze(['unavailable', 'available', 'submitted', 'published']);

/** Messages des refus du service (le motif est toujours expliqué à l'auteur). */
export const ERROR_MESSAGES = Object.freeze({
  invalid_rating: t('review_form.errors.invalid_rating'),
  text_too_short: t('review_form.errors.text_too_short', { count: TEXT_MIN }),
  text_too_long: t('review_form.errors.text_too_long', { max: TEXT_MAX, lines: LINES_MAX }),
  invalid_text: t('review_form.errors.invalid_text'),
  text_contact: t('review_form.errors.text_contact'),
  text_secret: t('review_form.errors.text_secret'),
  text_link: t('review_form.errors.text_link'),
  already_reviewed: t('review_form.errors.already_reviewed'),
  not_delivered: t('review_form.errors.not_delivered'),
  not_paid: t('review_form.errors.not_paid'),
  ticket_not_found: t('review_form.errors.ticket_not_found'),
  invalid_ticket: t('review_form.errors.invalid_ticket'),
  rate_limited: t('review_form.errors.rate_limited'),
  busy: t('review_form.errors.busy'),
  reviews_busy: t('review_form.errors.reviews_busy'),
  reviews_unavailable: t('review_form.errors.reviews_unavailable'),
  reviews_disabled: t('review_form.errors.reviews_disabled'),
  unauthorized: t('review_form.errors.unauthorized'),
  member_cooldown: t('review_form.errors.member_cooldown'),
  not_member: t('review_form.errors.not_member'),
  staff_review_forbidden: t('review_form.errors.staff_review_forbidden'),
  member_restricted: t('review_form.errors.member_restricted'),
  discord_unavailable: t('review_form.errors.discord_unavailable'),
  /* Texte trop long refusé avant la lecture (corps de la requête) : même consigne. */
  payload_too_large: t('review_form.errors.text_too_long', { max: TEXT_MAX, lines: LINES_MAX }),
});
/** « Vous pourrez laisser un nouvel avis à partir du … » (date lue sur le service), sinon le message générique. */
export function cooldownMessage(availableAt) {
  const time = Date.parse(availableAt || '');
  return Number.isFinite(time) ? t('review_form.errors.cooldown_until', { date: date(time, { dateStyle: 'long' }) }) : ERROR_MESSAGES.member_cooldown;
}

/** Intention « avis général » gardée pendant la connexion : vraie si elle a moins de 10 min. */
/**
 * Vrai seulement si l'intention a moins de 10 min ET si la connexion revenue est celle lancée par le bouton : `authAt`
 * (date de l'intention de fs-account.js au retour) doit être celle gardée au départ. Une connexion lancée ensuite depuis
 * l'en-tête n'ouvre donc jamais le formulaire d'avis.
 */
export function readMemberIntent(raw, now = Date.now(), authAt = undefined) {
  try {
    const intent = JSON.parse(raw || 'null');
    if (!intent || !Number.isFinite(intent.at) || intent.at > now + 60_000 || now - intent.at >= MEMBER_INTENT_MAX_MS) return false;
    return authAt === undefined || (Number.isFinite(intent.authAt) && intent.authAt === authAt);
  } catch { return false; }
}
/** Intention de connexion de fs-account.js (sessionStorage), dont la date sert d'empreinte de la connexion lancée. */
const AUTH_INTENT_KEY = 'fs-auth-intent';

/** GET /me → { reviews, memberReview: { available, availableAt } | null } (memberReview absent : avis généraux coupés). */
export function readMemberStatus(body) {
  const features = body?.features && typeof body.features === 'object' ? body.features : {};
  const member = features.memberReview && typeof features.memberReview === 'object' ? features.memberReview : null;
  const at = member && Number.isFinite(Date.parse(member.availableAt ?? '')) ? member.availableAt : null;
  const last = member?.lastReview && REVIEW_ID.test(member.lastReview.id ?? '') && ['pending', 'published', 'abandoned'].includes(member.lastReview.status) ? { id: member.lastReview.id, status: member.lastReview.status } : null;
  return { reviews: features.reviews === true, memberReview: member ? {
    available: typeof member.available === 'boolean' ? member.available : !at, availableAt: at,
    ...(member.reason === 'staff' ? { staff: true } : {}), ...(last ? { lastReview: last } : {})
  } : null };
}

export const FEATURES_KEY = 'fs-review-features';
export const FEATURES_TTL_MS = 5 * 60 * 1000;

/** Cache d'onglet de GET /features { at, member } : `member` (booléen) s'il a moins de 5 min, sinon null. */
export function readFeaturesCache(raw, now = Date.now()) {
  try {
    const cache = JSON.parse(raw || 'null');
    if (!cache || typeof cache.member !== 'boolean' || !Number.isFinite(cache.at) || cache.at > now + 1000 || now - cache.at >= FEATURES_TTL_MS) return null;
    return cache.member;
  } catch { return null; }
}

/** Invitation au serveur (discord.gg/<code> ou discord.com/invite/<code>), sinon ''. */
export function inviteUrl(raw) {
  const text = String(raw || '').trim();
  return /^https:\/\/(?:discord\.gg|discord\.com\/invite)\/[A-Za-z0-9-]{2,64}\/?$/.test(text) ? text : '';
}

/**
 * Connexion refusée au retour d'une demande d'avis général (fs_error) → { text, invite } : message neutre, jamais
 * « erreur » ; `invite` vrai si le lien d'invitation au serveur aide (compte hors du serveur).
 */
export function memberLoginMessage(code) {
  const text = String(code || '').toLowerCase();
  if (/restricted|timeout|pending/.test(text)) return { text: t('review_form.errors.member_restricted'), invite: false };
  if (/server_limit/.test(text)) return { text: t('review_form.login.server_limit'), invite: false };
  if (/member/.test(text)) return { text: t('review_form.errors.not_member'), invite: true };
  if (/denied/.test(text)) return { text: t('review_form.login.denied'), invite: false };
  return { text: t('review_form.login.failed'), invite: false };
}

/** GET /features (public, sans session) → { member, purchase } : ce que le thème peut proposer. Illisible : rien. */
export function readPublicFeatures(body) {
  const reviews = body?.reviews && typeof body.reviews === 'object' ? body.reviews : {};
  return { member: reviews.member === true, purchase: reviews.purchase === true };
}

/**
 * Ouverture « avis général » : 'login' (hors session), 'none' (avis coupés), 'cooldown' (délai en cours, sans achat
 * à évaluer), 'choose' (achat à évaluer ET avis général possible), 'purchase' (seulement l'achat), 'member'.
 */
export function memberPlan({ signedIn = false, reviews = false, memberReview = null, purchases = 0, now = Date.now() } = {}) {
  if (!signedIn) return 'login';
  if (!reviews) return 'none';
  // Équipe Space Syndicate : aucun avis (art. L121-4), ni général ni d'achat.
  if (memberReview?.staff) return 'staff';
  const memberOpen = Boolean(memberReview) && memberReview.available !== false && !(Date.parse(memberReview.availableAt ?? '') > now);
  if (purchases > 0) return memberOpen ? 'choose' : 'purchase';
  if (!memberReview) return 'none';
  return memberOpen ? 'member' : 'cooldown';
}
/** Message affiché depuis le code de refus du service (jamais son texte) ; code inconnu : message générique. */
export const errorMessage = (code) => (Object.hasOwn(ERROR_MESSAGES, code ?? '') ? ERROR_MESSAGES[code] : t('review_form.errors.generic'));

/** Dossier demandé par l'adresse (?avis=<dossier>), sinon ''. */
export function avisParam(search) {
  try { const value = (new URLSearchParams(search || '').get('review') ?? new URLSearchParams(search || '').get('avis')) || ''; return TICKET.test(value) ? value : ''; } catch { return ''; }
}

/**
 * Ce que la page fait au démarrage : 'login' (lien d'avis sans session : connexion seulement), 'open' (formulaire ou
 * état de l'achat), 'menu' (entrée « Laisser un avis » si besoin) ou 'none' (avis coupés : rien du tout).
 */
export function startPlan({ signedIn = false, reviews = false, ticket = '' } = {}) {
  if (!signedIn) return TICKET.test(ticket) ? 'login' : 'none';
  if (!reviews) return 'none';
  return TICKET.test(ticket) ? 'open' : 'menu';
}

/** Empreinte courte d'un jeton (FNV-1a), pour lier le cache d'onglet à la session sans y recopier le jeton. */
export function tokenKey(token) {
  let hash = 0x811c9dc5;
  for (const char of String(token || '')) { hash ^= char.codePointAt(0); hash = Math.imul(hash, 0x01000193) >>> 0; }
  return hash.toString(16).padStart(8, '0');
}

/** Cache d'onglet { key, at, rows } → lignes encore valides pour cette session, sinon null. */
export function readRowsCache(raw, key, now = Date.now()) {
  try {
    const cache = JSON.parse(raw || 'null');
    if (!cache || cache.key !== key || !Number.isFinite(cache.at) || cache.at > now + 1000 || now - cache.at >= ROWS_TTL_MS || !Array.isArray(cache.rows)) return null;
    return reviewRows({ tickets: cache.rows.map((row) => ({ ...row, review: { status: row.status, id: row.id, purchased: row.purchasedRaw, channelUrl: row.channelUrl, opensAt: row.opensAt } })) });
  } catch { return null; }
}

/** « 2026-10 » → « octobre 2026 » (mois et année dans la langue de la page) ; '' sinon. */
export function purchasedLabel(value) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value || ''));
  if (!match || Number(match[2]) < 1 || Number(match[2]) > 12) return '';
  return date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1), { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Salon #reviews du serveur, sinon ''. */
export function channelUrl(raw) {
  const text = String(raw || '').trim();
  return /^https:\/\/discord\.com\/channels\/\d{17,20}\/\d{17,20}$/.test(text) ? text : '';
}

/**
 * GET /tickets/mine → achats et état de leur avis { ticket, reference, title, game, gameName, status, id, purchased,
 * purchasedRaw, channelUrl, opensAt }. Sans clé « review » (avis coupés côté service) : liste vide.
 */
export function reviewRows(body) {
  const list = Array.isArray(body?.tickets) ? body.tickets : [];
  return list.filter((item) => item && TICKET.test(item.ticket ?? '') && item.review && REVIEW_STATES.includes(item.review.status)).map((item) => ({
    ticket: item.ticket, reference: String(item.reference ?? '').slice(0, 40), title: String(item.title ?? '').slice(0, 160),
    game: String(item.game ?? ''), gameName: GAME_NAMES[item.game] || '', status: item.review.status,
    id: REVIEW_ID.test(item.review.id ?? '') ? item.review.id : '', purchased: purchasedLabel(item.review.purchased),
    purchasedRaw: purchasedLabel(item.review.purchased) ? item.review.purchased : '', channelUrl: channelUrl(item.review.channelUrl),
    opensAt: Number.isFinite(Date.parse(item.review.opensAt ?? '')) ? item.review.opensAt : '',
  }));
}

// Même nettoyage et mêmes motifs que le service (service/reviews.mjs, revue croisée I1 et I2) : un premier avis avant
// l'envoi ; le service tranche.
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;
const FORMAT = /(?!\u200d)\p{Cf}/u;
const COMBINING = /\p{Mn}{3,}/u;
const FILLER = /[\u115f\u1160\u3164\uffa0\u2800]/;
const PICTO = /\p{Extended_Pictographic}/u;
const EMOJI_TAIL = /[\ufe0f\u{1f3fb}-\u{1f3ff}]/u;
const INVISIBLE = /[\p{Cf}\p{Mn}\u115f\u1160\u3164\uffa0\u2800]/gu;
const VISIBLE = /[\p{L}\p{N}\p{Extended_Pictographic}]/gu;
const LINK = /(?:https?:\/\/|www\.|discord(?:app)?\.(?:gg|com\/invite|me)|dsc\.gg|\bt\.me\b|telegram\.me|@everyone|@here|<[@#:]|<a?:\w+:\d+>|<t:\d+|\]\()/i;
const EMAIL = /[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/i;
const PHONE = /(?<!\d[\s.,]?)(?:\+\d{1,3}|0)[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d){6,12}(?!\d)/g;
const THOUSANDS = /^\+\d{1,3}(?:[\s.]\d{3})+$/;
const ROUND = /[\s.]000$/;
const GAME_UNIT = /^\s*(?:cr[ée]dits?|credits?|cr\b|uridium|uri\b|honou?r|honneur|xp\b|exp[ée]rience|experience|exp\b|points?\b|pts\b|rp\b|ep\b|rangs?\b|cubes?|jackpot|logdisks?|keys?\b|clés?\b|gold|or\b|munitions?|ammo)/i;
const DATE = /(?<!\d[./-]?)(?:(?:0?[1-9]|[12]\d|3[01])([./-])(?:0?[1-9]|1[0-2])\1(?:\d{4}|\d{2})|\d{4}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01]))(?![./-]?\d)(?![\s.-]\d{2}[\s.-]\d{2})/g;
const SECRET_LABEL = /\b(?:password|passwd|pwd|pass|mot de passe|mdp|secret|token|credentials?|login|identifiant)\s*[:=]/i;
const DISCORD_TOKEN = /[\w-]{23,28}\.[\w-]{6,7}\.[\w-]{27,}/;
const LONG_RUN = /[A-Za-z0-9_+/=-]{32,}/g;

function joinersAllowed(text) {
  const points = [...text];
  for (let i = 0; i < points.length; i++) {
    if (points[i] !== '\u200d') continue;
    let before = i - 1;
    while (before >= 0 && EMOJI_TAIL.test(points[before])) before--;
    if (before < 0 || !PICTO.test(points[before]) || !PICTO.test(points[i + 1] ?? '')) return false;
  }
  return true;
}
const bigNumber = (found, after) => THOUSANDS.test(found) && (ROUND.test(found) || GAME_UNIT.test(after));
function hasPhone(text) {
  const plain = text.replace(DATE, ' ');
  return [...plain.matchAll(PHONE)].some((match) => !bigNumber(match[0], plain.slice(match.index + match[0].length)));
}
const hasLongSecret = (text) => (text.match(LONG_RUN) ?? []).some((run) => /[A-Za-z]/.test(run) && /\d/.test(run));
function screen(text) {
  if (EMAIL.test(text) || hasPhone(text)) return 'text_contact';
  if (SECRET_LABEL.test(text) || DISCORD_TOKEN.test(text) || hasLongSecret(text)) return 'text_secret';
  if (LINK.test(text)) return 'text_link';
  return null;
}

/** Texte saisi → { text } nettoyé comme par le service (NFKC, espaces, 2 lignes vides au plus), ou { error }. */
export function cleanText(raw) {
  if (typeof raw !== 'string') return { error: 'invalid_text' };
  const normalized = raw.normalize('NFKC').replace(/\r\n?/g, '\n');
  if (CONTROL.test(normalized) || FORMAT.test(normalized) || COMBINING.test(normalized) || FILLER.test(normalized) || !joinersAllowed(normalized)) return { error: 'invalid_text' };
  const text = normalized.split('\n').map((line) => line.replace(/[\t \u00a0\u2000-\u200a\u202f\u205f\u3000]+/g, ' ').trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  const length = [...text].length;
  if (length < TEXT_MIN || (text.match(VISIBLE) ?? []).length < TEXT_MIN) return { error: 'text_too_short' };
  if (length > TEXT_MAX || text.split('\n').length > LINES_MAX) return { error: 'text_too_long' };
  const problem = screen(text) ?? screen(text.replace(INVISIBLE, ''));
  return problem ? { error: problem } : { text };
}

/** Longueur affichée par le compteur : points de code après NFKC, comme la borne du service. */
export const textLength = (raw) => [...String(raw ?? '').normalize('NFKC').replace(/\r\n?/g, '\n').trim()].length;

/** Brouillon { rating, text } → code de refus, ou null. */
export function draftProblem({ rating, text }) {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) return 'invalid_rating';
  return cleanText(text).error ?? null;
}

/** Annonce du compteur aux lecteurs d'écran : seulement en franchissant 100, 50, 20 et 0 caractères restants. */
export function counterAnnouncement(previous, length) {
  const before = TEXT_MAX - previous, after = TEXT_MAX - length;
  const crossed = [100, 50, 20, 0].find((step) => before > step && after <= step);
  if (crossed === undefined) return '';
  return after < 0 ? t('review_form.counter.over', { count: -after, n: number(-after) }) : t('review_form.counter.left', { count: after, n: number(after) });
}

/** Réponse de POST /reviews → { ok, published?, id?, channelUrl?, message }. */
export function submitOutcome(status, body) {
  if (status === 201 && REVIEW_ID.test(body?.review?.id ?? '')) {
    const published = body.review.status === 'published';
    const member = body.review.id.startsWith('m');
    return {
      ok: true, published, id: body.review.id, channelUrl: channelUrl(body.review.channelUrl),
      message: member
        ? t(published ? 'review_form.outcome.published' : 'review_form.outcome.saved')
        : t(published ? 'review_form.outcome.published_verified' : 'review_form.outcome.saved'),
    };
  }
  const code = status === 401 ? 'unauthorized' : body?.error;
  return { ok: false, code: String(code || ''), message: code === 'member_cooldown' ? cooldownMessage(body?.availableAt) : errorMessage(code) };
}

/** GET /tickets/mine → { ok, rows } ou { ok: false, code } (401 : session expirée ; autre : avis indisponibles). */
export function rowsOutcome(status, body) {
  if (status === 200) return { ok: true, rows: reviewRows(body) };
  return { ok: false, code: status === 401 ? 'unauthorized' : 'reviews_unavailable' };
}

/* ------------------------------------------------------------------ interface */

/** Logo Discord (masque CSS de fs-components.css), décoratif. */
const discordIcon = () => h('span', { class: 'fs-discord-icon', 'aria-hidden': 'true' });
const tabStore = {
  get(key) { try { return window.sessionStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { window.sessionStorage.setItem(key, value); return true; } catch { return false; } },
  remove(key) { try { window.sessionStorage.removeItem(key); } catch { /* indisponible */ } },
};
const formatDate = (iso) => { const time = Date.parse(iso || ''); return Number.isFinite(time) ? date(time, { dateStyle: 'medium' }) : ''; };
const external = () => h('span', { class: 'fs-visually-hidden', text: ` ${t('review_form.dialog.new_tab')}` });

function createReviews(account) {
  let dialog = null;
  let sequence = 0;

  /** Avis de l'acheteur : cache d'onglet de 60 s lié à la session ; seulement après /me → reviews. */
  async function loadRows(force = false) {
    const active = account.session();
    if (!active) return { ok: false, code: 'unauthorized' };
    const key = tokenKey(active.token);
    if (!force) { const cached = readRowsCache(tabStore.get(ROWS_KEY), key); if (cached) return { ok: true, rows: cached }; }
    const { status, body } = await account.call('/tickets/mine');
    const outcome = rowsOutcome(status, body);
    if (outcome.ok) tabStore.set(ROWS_KEY, JSON.stringify({ key, at: Date.now(), rows: outcome.rows }));
    else tabStore.remove(ROWS_KEY);
    return outcome;
  }
  const forget = () => tabStore.remove(ROWS_KEY);

  /* ---- dialogue du catalogue (fs-ui-dialog.js) : focus rendu à l'élément qui l'a ouvert (bouton « Laisser un avis »).
     Hôte : la page (.fs-demo), jamais le conteneur masqué de la bannière de cookies. ---- */
  function ensureDialog() {
    if (dialog?.node.isConnected) return dialog;
    const box = uiDialog({ eyebrow: t('review_form.dialog.eyebrow_purchase'), close: t('review_form.dialog.close'), remove: false, attrs: { 'data-fs-review-dialog': true },
      bodyAttrs: { 'aria-live': 'polite' } });
    dialog = { node: box.node, box, title: box.title, body: box.body, eyebrow: box.eyebrow };
    return dialog;
  }
  let returnTo = null; // élément à refocaliser à la fermeture (FSReviews.open({ from }))
  let context = 'purchase'; // sur-titre du dialogue : achat (lien d'avis, menu) ou avis général (FSReviews.open)
  function show(titleText, ...content) {
    const view = ensureDialog();
    view.eyebrow.textContent = t(context === 'member' ? 'review_form.dialog.eyebrow_member' : 'review_form.dialog.eyebrow_purchase');
    view.title.textContent = titleText;
    view.body.replaceChildren(...content.filter((child) => child !== null && child !== undefined && child !== false));
    view.box.open({ opener: returnTo, host: dialogHost() });
    return view;
  }
  const note = (text) => h('p', { class: 'fs-rv-note', text });

  /** Lien d'avis sans session : connexion seulement, sans promettre que l'avis est possible. */
  function loginPrompt(ticket) {
    context = 'purchase';
    show(t('review_form.login_prompt.title'),
      note(t('review_form.login_prompt.text')),
      h('p', { class: 'fs-rv-actions' }, h('button', { type: 'button', class: 'fs-btn fs-btn--primary', onclick: () => {
        if (!account.login({ kind: 'account', ticket })) dialog.body.append(note(t('review_form.login_prompt.storage')));
      } }, discordIcon(), t('review_form.login_prompt.button'))));
  }

  function chooser(list) {
    show(t('review_form.choose.title'), h('ul', { class: 'fs-rv-list' }, list.map((row) => h('li', { class: 'fs-rv-row' },
      h('p', { class: 'fs-rv-row-main' }, h('span', { class: 'fs-rv-ref', text: row.reference || '—' }), row.gameName ? ` · ${row.gameName}` : '', row.purchased ? ` · ${t('review_form.choose.purchased', { month: row.purchased })}` : ''),
      h('button', { type: 'button', class: 'fs-btn fs-btn--primary', onclick: () => form(row) }, t('review_form.choose.give'))))));
  }

  /** Ouvre l'avis du dossier `ticket` : formulaire, ou état (déjà publié, pas encore ouvert, introuvable). */
  async function openFor(ticket) {
    context = 'purchase';
    show(t('review_form.form.title'), note(t('review_form.dialog.loading')));
    let outcome;
    try { outcome = await loadRows(true); } catch { outcome = { ok: false, code: '' }; }
    if (!outcome.ok) return show(t('review_form.form.title'), note(errorMessage(outcome.code)));
    const row = outcome.rows.find((item) => item.ticket === ticket);
    if (!row) return show(t('review_form.form.title'), note(errorMessage('ticket_not_found')));
    if (row.status === 'available') return form(row);
    if (row.status === 'unavailable') {
      return show(t('review_form.form.title'), note(row.opensAt ? t('review_form.form.opens_at', { date: formatDate(row.opensAt) }) : errorMessage('not_delivered')));
    }
    return done({ published: row.status === 'published', id: row.id, channelUrl: row.channelUrl, message: errorMessage('already_reviewed') });
  }

  function done(outcome) {
    show(t('review_form.form.thanks'),
      note(outcome.message),
      outcome.id ? h('p', { class: 'fs-rv-note' }, `${t('review_form.form.review_id')} `, h('span', { class: 'fs-rv-ref', text: outcome.id })) : null);
  }

  function form(row) {
    const id = `fs-rv-form-${++sequence}`;
    const error = h('div', { class: 'fs-alert fs-rv-alert', 'data-tone': 'danger', role: 'alert', hidden: true, tabindex: '-1' });
    const stars = h('div', { class: 'fs-rv-stars' }, [1, 2, 3, 4, 5].map((value) => h('label', { class: 'fs-rv-star' },
      h('input', { type: 'radio', name: `${id}-rating`, value: String(value), class: 'fs-rv-star-input' }),
      h('span', { class: 'fs-rv-star-mark', 'aria-hidden': 'true', text: '★' }),
      h('span', { class: 'fs-visually-hidden', text: t('review_form.form.star', { count: value }) }))));
    const rating = h('fieldset', { class: 'fs-field fs-rv-rating', role: 'radiogroup', 'aria-required': 'true', 'aria-labelledby': `${id}-legend` },
      h('legend', { class: 'fs-field-label', id: `${id}-legend`, text: t('review_form.form.rating') }), stars);
    /* Compteur visible (non annoncé) ; une région séparée n'annonce que les seuils proches de la limite. */
    const counter = h('p', { class: 'fs-field-hint fs-rv-counter', id: `${id}-count`, text: `0 / ${TEXT_MAX}` });
    const spoken = h('p', { class: 'fs-visually-hidden', 'aria-live': 'polite' });
    let lastLength = 0;
    /* Pas de maxlength : le navigateur compte en unités UTF-16, le service en points de code (NFKC). */
    const textarea = h('textarea', { class: 'fs-textarea fs-rv-text', id: `${id}-text`, name: 'text', rows: '6', required: true, 'aria-describedby': `${id}-count ${id}-notice`,
      oninput: () => {
        const length = textLength(textarea.value);
        counter.textContent = `${length} / ${TEXT_MAX}`;
        counter.toggleAttribute('data-over', length > TEXT_MAX);
        const announce = counterAnnouncement(lastLength, length);
        if (announce) spoken.textContent = announce;
        lastLength = length;
      } });
    const member = row.kind === 'member';
    const notice = h('p', { class: 'fs-field-hint fs-rv-notice', id: `${id}-notice`,
      text: t(member ? 'review_form.form.notice_member' : 'review_form.form.notice_purchase') });
    const submit = h('button', { type: 'submit', class: 'fs-btn fs-btn--primary fs-rv-submit' }, t('review_form.form.publish'));
    const node = h('form', { class: 'fs-rv-form', novalidate: true },
      member && row.abandoned ? h('p', { class: 'fs-alert fs-rv-note', 'data-tone': 'warning', role: 'status', text: t('review_form.form.abandoned', { id: row.abandoned }) }) : null,
      member ? h('p', { class: 'fs-rv-meta', text: t('review_form.form.general_meta') })
        : h('p', { class: 'fs-rv-meta' }, h('span', { class: 'fs-badge fs-rv-verified', text: t('review_form.form.verified') }), row.gameName ? ` ${row.gameName}` : '', row.purchased ? ` · ${row.purchased}` : ''),
      rating, h('div', { class: 'fs-field' }, h('label', { class: 'fs-field-label', for: `${id}-text`, text: t('review_form.form.title') }), textarea, counter, spoken), notice, error,
      h('p', { class: 'fs-rv-actions' }, submit));
    let busy = false;
    const fail = (message) => { error.textContent = message; error.hidden = false; error.focus({ preventScroll: false }); };
    node.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy) return;
      const draft = { rating: Number(node.querySelector('input[type="radio"]:checked')?.value), text: textarea.value };
      const problem = draftProblem(draft);
      if (problem) return fail(errorMessage(problem));
      busy = true; submit.disabled = true; submit.textContent = t('review_form.form.sending'); error.hidden = true;
      try {
        const payload = member ? { kind: 'member', rating: draft.rating, text: draft.text } : { ticket: row.ticket, rating: draft.rating, text: draft.text };
        const { status, body } = await account.call('/reviews', { method: 'POST', body: payload });
        const outcome = submitOutcome(status, body);
        if (outcome.ok || outcome.code === 'already_reviewed') { forget(); refreshMenus(); return done(outcome.ok ? outcome : { message: outcome.message }); }
        if (outcome.code === 'member_cooldown') return show(t('review_form.form.title'), note(outcome.message));
        fail(outcome.message);
      } catch { fail(errorMessage('')); }
      finally { busy = false; submit.disabled = false; submit.textContent = t('review_form.form.publish'); }
    });
    show(t('review_form.form.title'), node);
  }

  /* ---- avis général (membre) : window.FSReviews.open({ kind: 'member' }) ---- */
  async function openMember() {
    context = 'member';
    if (!account.session()) {
      /* Connexion Discord d'abord ; le formulaire s'ouvre au retour (intention d'onglet, 10 min). */
      const url = account.prepareLogin({ kind: 'account' });
      let authAt = null;
      try { authAt = JSON.parse(tabStore.get(AUTH_INTENT_KEY) || 'null')?.at ?? null; } catch { authAt = null; }
      if (url && Number.isFinite(authAt) && tabStore.set(MEMBER_INTENT_KEY, JSON.stringify({ at: Date.now(), authAt }))) { window.location.assign(url); return; }
      tabStore.remove(MEMBER_INTENT_KEY);
      return show(t('review_form.choose.title'), note(t('review_form.login_prompt.storage_tab')));
    }
    show(t('review_form.choose.title'), note(t('review_form.dialog.loading')));
    let status;
    try { const reply = await account.call('/me'); status = reply.status === 200 ? readMemberStatus(reply.body) : null; } catch { status = null; }
    if (!status) return show(t('review_form.choose.title'), note(errorMessage('')));
    let purchases = [];
    if (status.reviews) { try { const outcome = await loadRows(); purchases = outcome.ok ? outcome.rows.filter((row) => row.status === 'available') : []; } catch { purchases = []; } }
    const plan = memberPlan({ signedIn: true, reviews: status.reviews, memberReview: status.memberReview, purchases: purchases.length });
    if (plan === 'none') return show(t('review_form.choose.title'), note(errorMessage('reviews_disabled')));
    if (plan === 'staff') return show(t('review_form.choose.title'), note(errorMessage('staff_review_forbidden')));
    if (plan === 'cooldown') return show(t('review_form.choose.title'), note(cooldownMessage(status.memberReview?.availableAt)));
    if (plan === 'member') return form({ kind: 'member', abandoned: status.memberReview?.lastReview?.status === 'abandoned' ? status.memberReview.lastReview.id : '' });
    if (plan === 'purchase') return purchases.length === 1 ? form(purchases[0]) : chooser(purchases);
    /* Achat à évaluer et avis général possible : le choix est proposé. */
    show(t('review_form.choose.title'), h('ul', { class: 'fs-rv-list' },
      purchases.map((row) => h('li', { class: 'fs-rv-row' },
        h('p', { class: 'fs-rv-row-main' }, h('span', { class: 'fs-badge fs-rv-verified', text: t('review_form.form.verified') }), ' ', h('span', { class: 'fs-rv-ref', text: row.reference || '—' }), row.gameName ? ` · ${row.gameName}` : '', row.purchased ? ` · ${t('review_form.choose.purchased', { month: row.purchased })}` : ''),
        h('button', { type: 'button', class: 'fs-btn fs-btn--primary', onclick: () => form(row) }, t('review_form.choose.this_purchase')))),
      h('li', { class: 'fs-rv-row' }, h('p', { class: 'fs-rv-row-main', text: t('review_form.choose.general_row') }),
        h('button', { type: 'button', class: 'fs-btn', onclick: () => form({ kind: 'member' }) }, t('review_form.choose.general')))));
  }

  /** Message neutre après une connexion refusée, avec le lien d'invitation au serveur s'il est configuré et utile. */
  function membershipNotice({ text, invite }) {
    context = 'member';
    const link = invite ? inviteUrl(document.querySelector('[data-fs-review-open][data-fs-discord-invite]')?.getAttribute('data-fs-discord-invite')
      || document.querySelector('[data-fs-discord-invite]')?.getAttribute('data-fs-discord-invite')) : '';
    show(t('review_form.choose.title'), note(text),
      link ? h('p', { class: 'fs-rv-actions' }, h('a', { class: 'fs-btn fs-btn--primary', href: link, target: '_blank', rel: 'noopener noreferrer' }, `${t('review_form.choose.join')} `, h('span', { 'aria-hidden': 'true', text: '↗' }), external())) : null);
  }

  /** API publique : { kind: 'member', from } (avis général) ou { ticket, from } (avis d'un achat). */
  function open(options = {}) {
    returnTo = typeof Element !== 'undefined' && options?.from instanceof Element ? options.from : null;
    if (options?.kind === 'member') return openMember();
    if (TICKET.test(options?.ticket ?? '')) return account.session() ? openFor(options.ticket) : loginPrompt(options.ticket);
    return openMember();
  }

  /* ---- menu du compte : « Laisser un avis » quand un avis est attendu ---- */
  const removeMenus = () => document.querySelectorAll('[data-fs-review-menu]').forEach((item) => item.remove());
  async function refreshMenus() {
    let list = [];
    try { const outcome = await loadRows(); list = outcome.ok ? outcome.rows.filter((row) => row.status === 'available') : []; } catch { list = []; }
    removeMenus();
    if (!list.length) return;
    document.querySelectorAll('[data-fs-account-menu] .fs-account-items').forEach((items) => {
      const item = h('li', { 'data-fs-review-menu': true }, h('button', { type: 'button', class: 'fs-account-item', onclick: () => { context = 'purchase'; return list.length === 1 ? form(list[0]) : chooser(list); } },
        list.length === 1 ? t('review_form.choose.title') : t('review_form.choose.menu_count', { count: list.length })));
      /* Avant « Se déconnecter », toujours en dernier. */
      items.insertBefore(item, items.querySelector('[data-fs-account-logout]')?.closest('li') || null);
    });
  }

  /* ---- démarrage et changements de session ---- */
  async function refresh(ticket = '') {
    const signedIn = Boolean(account.session());
    const rights = signedIn ? await account.me() : null;
    const plan = startPlan({ signedIn, reviews: rights?.reviews === true, ticket });
    if (plan !== 'open' && plan !== 'menu') removeMenus();
    if (plan === 'login') return loginPrompt(ticket);
    if (plan === 'none') return;
    if (plan === 'open') openFor(ticket);
    refreshMenus();
  }
  /* ---- boutons déclaratifs [data-fs-review-open] : masqués tant que le service n'annonce pas les avis membres ---- */
  let memberAnnounced = false;
  const applyButtons = (scope = document) => scope.querySelectorAll?.('[data-fs-review-open]').forEach((button) => { button.hidden = !memberAnnounced; });
  /** GET /features (public, avec ou sans session), 5 min de cache d'onglet : démasque les boutons si l'avis général est ouvert. */
  async function announce() {
    let on = readFeaturesCache(tabStore.get(FEATURES_KEY));
    if (on === null) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
        const response = await fetch(`${account.service}/features`, { mode: 'cors', credentials: 'omit', signal: controller.signal }).finally(() => clearTimeout(timer));
        on = response.ok && readPublicFeatures(await response.json().catch(() => null)).member;
        if (response.ok) tabStore.set(FEATURES_KEY, JSON.stringify({ at: Date.now(), member: on }));
      } catch { on = false; }
    }
    // Équipe Space Syndicate connectée : bouton masqué (aucun avis de l'équipe, art. L121-4).
    if (on && account.session()) { const rights = await account.me().catch(() => null); if (rights?.team) on = false; }
    memberAnnounced = on;
    applyButtons();
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('click', (event) => {
      const button = event.target?.closest?.('[data-fs-review-open]');
      if (!button || button.hidden) return;
      event.preventDefault();
      open({ kind: 'member', from: button });
    });
    /* Boutons rendus après ce module (section rechargée, carrousel) : même visibilité. */
    new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches('[data-fs-review-open]')) node.hidden = !memberAnnounced;
      applyButtons(node);
    }))).observe(document.documentElement, { childList: true, subtree: true });
  }

  async function start() {
    announce();
    const wanted = avisParam(window.location.search);
    if (wanted) {
      /* L'adresse est nettoyée : un rechargement ne rouvre pas le formulaire. */
      try { const url = new URL(window.location.href); url.searchParams.delete('review'); url.searchParams.delete('avis'); history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`); } catch { /* adresse inchangée */ }
    }
    /* Retour de connexion lancée depuis un lien d'avis : intention « account » portant le dossier (fs-account.js). */
    let pending = '', memberWanted = false, memberRefused = null;
    if (account.returning) {
      const outcome = await account.returning.promise.catch(() => null);
      const intent = outcome?.intent ?? account.returning.intent;
      if (outcome?.ok && intent?.kind === 'account' && TICKET.test(intent.ticket ?? '')) pending = intent.ticket;
      /* Avis général demandé avant la connexion : intention d'onglet, consommée une fois. Connexion réussie : formulaire ;
       * refusée (compte hors du serveur, restreint, annulée) : message neutre. */
      const asked = !pending && readMemberIntent(tabStore.get(MEMBER_INTENT_KEY), Date.now(), Number.isFinite(intent?.at) ? intent.at : null);
      memberWanted = asked && Boolean(outcome?.ok);
      if (asked && !outcome?.ok) memberRefused = memberLoginMessage(outcome?.error);
    }
    tabStore.remove(MEMBER_INTENT_KEY);
    await refresh(wanted || pending);
    if (memberWanted && !wanted) openMember();
    if (memberRefused && !wanted) membershipNotice(memberRefused);
    document.addEventListener('fs:account', (event) => { if (event.detail?.state === 'out') forget(); refresh(); announce(); });
  }
  start();
  return Object.freeze({ open });
}

/* Démarrage indépendant de l'ordre de chargement : si fs-account.js n'a pas encore posé window.FSAccount, nouvel essai
 * quand le document est prêt (tous les modules exécutés), puis au chargement complet. Sans FSAccount : rien, les
 * boutons [data-fs-review-open] restent masqués. */
function boot() {
  if (window.FSReviews) return true;
  if (!window.FSAccount?.service) return false;
  window.FSReviews = createReviews(window.FSAccount);
  /* Les scripts qui appellent FSReviews.open peuvent attendre cet événement s'ils sont chargés avant ce module. */
  document.dispatchEvent(new CustomEvent('fs:reviews-ready'));
  return true;
}
if (typeof document !== 'undefined' && !boot()) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  window.addEventListener('load', boot, { once: true });
}
